import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ReconciliationBucket,
  ReferralReconciliationRow,
  getReferralReconciliation,
} from '@/services/referralReconciliationService';

// Read-only reconciliation board. For each referring dentist it shows how many
// of their referred patients actually turned up (matched against the Plato
// queue history) so reception and the dentist can see who came without a
// WhatsApp trace-back. No writes happen here — purely a view over the
// get_referral_reconciliation RPC.

const BUCKET_META: Record<
  ReconciliationBucket,
  { label: string; badgeClassName: string; order: number }
> = {
  arrived: {
    label: 'Arrived / Seen',
    badgeClassName: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    order: 1,
  },
  booked: {
    label: 'Booked',
    badgeClassName: 'border-blue-300 bg-blue-50 text-blue-900',
    order: 2,
  },
  awaiting: {
    label: 'Awaiting booking',
    badgeClassName: 'border-amber-300 bg-amber-50 text-amber-900',
    order: 3,
  },
  never_booked: {
    label: 'Never booked',
    badgeClassName: 'border-rose-300 bg-rose-50 text-rose-900',
    order: 4,
  },
  did_not_proceed: {
    label: 'Did not proceed / No-show',
    badgeClassName: 'border-orange-300 bg-orange-50 text-orange-900',
    order: 5,
  },
  referred_back: {
    label: 'Referred back',
    badgeClassName: 'border-violet-300 bg-violet-50 text-violet-900',
    order: 6,
  },
};

const BUCKET_ORDER = (Object.keys(BUCKET_META) as ReconciliationBucket[]).sort(
  (a, b) => BUCKET_META[a].order - BUCKET_META[b].order
);

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'd MMM yyyy');
  } catch {
    return value;
  }
};

interface DentistGroup {
  dentist: string;
  clinic: string | null;
  rows: ReferralReconciliationRow[];
  counts: Record<ReconciliationBucket, number>;
  /** % of referrals where the patient actually turned up and was treated
   *  (arrived + referred back). Khine 9 Apr: "a lot of people refer, but then
   *  they don't test… whether able to convert". Null when too few to judge. */
  conversionPct: number | null;
  /** Referrals received in the last 30 days. */
  recent30: number;
  /** Average referrals/month over the prior 6 months (excluding the last 30d). */
  monthlyAvg: number | null;
  /** True when recent volume fell 40%+ below the 6-month average. */
  dropOff: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const buildGroups = (rows: ReferralReconciliationRow[]): DentistGroup[] => {
  const map = new Map<string, DentistGroup>();
  const now = Date.now();
  const cut30 = now - 30 * DAY_MS;
  const cut210 = now - 210 * DAY_MS; // 30d + 6 months of baseline

  for (const row of rows) {
    const dentist = row.referring_doctor?.trim() || 'Unattributed referrals';
    let group = map.get(dentist);
    if (!group) {
      group = {
        dentist,
        clinic: row.referring_clinic,
        rows: [],
        counts: {
          arrived: 0,
          booked: 0,
          awaiting: 0,
          never_booked: 0,
          did_not_proceed: 0,
          referred_back: 0,
        },
        conversionPct: null,
        recent30: 0,
        monthlyAvg: null,
        dropOff: false,
      };
      map.set(dentist, group);
    }
    group.rows.push(row);
    group.counts[row.bucket] += 1;
  }

  for (const group of map.values()) {
    const total = group.rows.length;
    const converted = group.counts.arrived + group.counts.referred_back;
    group.conversionPct = total >= 3 ? Math.round((converted / total) * 100) : null;

    let recent30 = 0;
    let baseline = 0;
    for (const row of group.rows) {
      if (!row.referral_received_date) continue;
      const t = parseISO(row.referral_received_date).getTime();
      if (Number.isNaN(t)) continue;
      if (t >= cut30) recent30 += 1;
      else if (t >= cut210) baseline += 1;
    }
    group.recent30 = recent30;
    // Require a meaningful baseline (≥6 referrals over 6 months) before
    // calling a drop-off, so tiny referrers don't false-alarm.
    if (baseline >= 6) {
      const avg = baseline / 6;
      group.monthlyAvg = Math.round(avg * 10) / 10;
      group.dropOff = recent30 < avg * 0.6;
    }
  }

  return [...map.values()].sort((a, b) => {
    // Surface the biggest "who's missing" problem first: dentists with the most
    // never-booked cases pin to the top.
    const aGap = a.counts.never_booked + a.counts.awaiting;
    const bGap = b.counts.never_booked + b.counts.awaiting;
    if (aGap !== bGap) return bGap - aGap;
    return a.dentist.localeCompare(b.dentist);
  });
};

const ReferralReconciliation: React.FC = () => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['referral-reconciliation'],
    queryFn: () => getReferralReconciliation(14),
  });

  const groups = useMemo(() => buildGroups(data ?? []), [data]);

  const totals = useMemo(() => {
    const counts: Record<ReconciliationBucket, number> = {
      arrived: 0,
      booked: 0,
      awaiting: 0,
      never_booked: 0,
      did_not_proceed: 0,
      referred_back: 0,
    };
    for (const row of data ?? []) {
      counts[row.bucket] += 1;
    }
    return counts;
  }, [data]);

  const toggle = (dentist: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(dentist)) {
        next.delete(dentist);
      } else {
        next.add(dentist);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Expected vs arrived
          </h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Per referring dentist: who they referred vs who actually turned up
            (matched against the Plato queue). Read-only — no records change here.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Estate-wide totals */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="flex flex-wrap gap-2 py-4">
          {BUCKET_ORDER.map((bucket) => (
            <Badge
              key={bucket}
              variant="outline"
              className={cn('border', BUCKET_META[bucket].badgeClassName)}
            >
              {BUCKET_META[bucket].label}: {totals[bucket]}
            </Badge>
          ))}
        </CardContent>
      </Card>

      {isLoading && (
        <p className="text-sm text-slate-500">Loading reconciliation…</p>
      )}

      {isError && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="flex items-center gap-2 py-4 text-sm text-rose-900">
            <AlertTriangle className="h-4 w-4" />
            Could not load reconciliation:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && groups.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="flex items-center gap-2 py-4 text-sm text-slate-600">
            <CheckCircle2 className="h-4 w-4" />
            No referred cases to reconcile yet.
          </CardContent>
        </Card>
      )}

      {groups.map((group) => {
        const isOpen = expanded.has(group.dentist);
        return (
          <Card key={group.dentist} className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <button
                type="button"
                onClick={() => toggle(group.dentist)}
                className="flex w-full items-center justify-between gap-3 text-left"
              >
                <div className="flex items-center gap-2">
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                  <div>
                    <CardTitle className="text-base">{group.dentist}</CardTitle>
                    {group.clinic && (
                      <p className="text-xs text-slate-500">{group.clinic}</p>
                    )}
                  </div>
                </div>
                <span className="text-sm font-medium text-slate-500">
                  {group.rows.length} referral{group.rows.length === 1 ? '' : 's'}
                </span>
              </button>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {group.conversionPct !== null && (
                  <Badge
                    variant="outline"
                    className={cn(
                      'border px-1.5 py-0 text-[11px] font-semibold',
                      group.conversionPct >= 60
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                        : group.conversionPct >= 30
                          ? 'border-amber-300 bg-amber-50 text-amber-900'
                          : 'border-rose-300 bg-rose-50 text-rose-900'
                    )}
                  >
                    Conversion: {group.conversionPct}%
                  </Badge>
                )}
                {group.dropOff && (
                  <Badge
                    variant="outline"
                    className="border px-1.5 py-0 text-[11px] font-semibold border-rose-300 bg-rose-50 text-rose-900"
                  >
                    ⚠ Referrals dropping: {group.recent30} in last 30d vs ~{group.monthlyAvg}/mo
                  </Badge>
                )}
                {BUCKET_ORDER.filter((bucket) => group.counts[bucket] > 0).map(
                  (bucket) => (
                    <Badge
                      key={bucket}
                      variant="outline"
                      className={cn(
                        'border px-1.5 py-0 text-[11px] font-medium',
                        BUCKET_META[bucket].badgeClassName
                      )}
                    >
                      {BUCKET_META[bucket].label}: {group.counts[bucket]}
                    </Badge>
                  )
                )}
              </div>
            </CardHeader>

            {isOpen && (
              <CardContent className="pt-0">
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">Patient</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium">Referred</th>
                        <th className="px-3 py-2 font-medium">Last visit</th>
                        <th className="px-3 py-2 font-medium">Bucket</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {group.rows.map((row) => (
                        <tr key={row.case_id} className="text-slate-700">
                          <td className="px-3 py-2">
                            <span className="font-medium text-slate-900">
                              {row.patient_display_name}
                            </span>
                            {row.is_manual && (
                              <Badge
                                variant="outline"
                                className="ml-1.5 border-slate-300 bg-slate-100 px-1 py-0 text-[10px] text-slate-600"
                              >
                                Manual
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 capitalize">
                            {row.status.replace(/_/g, ' ')}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatDate(row.referral_received_date)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {row.last_visit_date ? (
                              <>
                                {formatDate(row.last_visit_date)}
                                {row.last_visit_status && (
                                  <span className="ml-1 text-xs text-slate-400">
                                    ({row.last_visit_status})
                                  </span>
                                )}
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Badge
                              variant="outline"
                              className={cn(
                                'border px-1.5 py-0 text-[11px] font-medium',
                                BUCKET_META[row.bucket].badgeClassName
                              )}
                            >
                              {BUCKET_META[row.bucket].label}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default ReferralReconciliation;
